import { Trans } from '@lingui/react/macro';
import { QueueListIcon } from '@heroicons/react/24/outline';
import type { BatchDraftItem } from '@pantry/shared';
import { GroupedBatchItemCard } from './GroupedBatchItemCard';

interface BatchItemListProps {
  items: BatchDraftItem[];
  isMultiSelectMode: boolean;
  selectedClientIds: Set<string>;
  onEditItem: (clientId: string) => void;
  onRemoveItem: (clientId: string) => void;
  onToggleSelect: (clientId: string) => void;
  onEditInstance: (clientId: string, instanceId: string) => void;
  onDeleteInstance: (clientId: string, instanceId: string) => void;
  onUpdateInstanceQuantity: (clientId: string, instanceId: string, quantity: number) => void;
  onAddBatch: (clientId: string) => void;
}

/**
 * List of draft items in batch entry.
 * Shows empty state when no items are added.
 * Uses GroupedBatchItemCard for expandable instances.
 */
export function BatchItemList({
  items,
  isMultiSelectMode,
  selectedClientIds,
  onEditItem,
  onRemoveItem,
  onToggleSelect,
  onEditInstance,
  onDeleteInstance,
  onUpdateInstanceQuantity,
  onAddBatch,
}: BatchItemListProps) {
  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center animate-in fade-in duration-300">
        <div className="h-16 w-16 rounded-2xl bg-muted/50 flex items-center justify-center mb-4">
          <QueueListIcon className="h-8 w-8 text-muted-foreground" />
        </div>
        <h3 className="font-display text-lg font-semibold text-foreground mb-1">
          <Trans>No items added yet</Trans>
        </h3>
        <p className="text-sm text-muted-foreground max-w-xs">
          <Trans>Scan barcodes or add items manually to build your batch</Trans>
        </p>
      </div>
    );
  }

  // Sort by addedAt descending (most recent first)
  const sortedItems = [...items].sort((a, b) => b.addedAt - a.addedAt);

  return (
    <div className="space-y-2">
      {sortedItems.map((item) => (
        <GroupedBatchItemCard
          key={item.clientId}
          item={item}
          isMultiSelectMode={isMultiSelectMode}
          isSelected={selectedClientIds.has(item.clientId)}
          onToggleSelect={() => onToggleSelect(item.clientId)}
          onEdit={() => onEditItem(item.clientId)}
          onDelete={() => onRemoveItem(item.clientId)}
          onEditInstance={(instanceId) => onEditInstance(item.clientId, instanceId)}
          onDeleteInstance={(instanceId) => onDeleteInstance(item.clientId, instanceId)}
          onUpdateInstanceQuantity={(instanceId, quantity) =>
            onUpdateInstanceQuantity(item.clientId, instanceId, quantity)
          }
          onAddBatch={() => onAddBatch(item.clientId)}
        />
      ))}
    </div>
  );
}
